'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Clock, Zap, Settings } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { cn } from '@/lib/utils'

interface QuickAction {
  id: string
  icon: React.ElementType
  label: string
  onClick?: () => void
  href?: string
}

interface QuickActionsWidgetProps {
  onCreateIssue?: () => void
  loading?: boolean
  variant?: 'dashboard' | 'mypage'
}

export function QuickActionsWidget({
  onCreateIssue,
  loading = false,
  variant = 'mypage'
}: QuickActionsWidgetProps) {
  const t = useTranslations()
  const tDashboard = useTranslations('dashboard')
  const tMyPage = useTranslations('myPage')

  const actions: QuickAction[] = variant === 'dashboard' 
    ? [
        {
          id: 'new-issue',
          icon: Plus,
          label: tDashboard('newIssue'),
          onClick: onCreateIssue
        },
        {
          id: 'log-time',
          icon: Clock,
          label: tDashboard('timeEntry'),
          href: '/time'
        }
      ]
    : [
        {
          id: 'create-issue',
          icon: Plus,
          label: tMyPage('createIssue'),
          onClick: onCreateIssue
        },
        {
          id: 'log-time',
          icon: Clock,
          label: tMyPage('logTime'),
          href: '/time'
        },
        {
          id: 'settings',
          icon: Settings,
          label: tMyPage('settings'),
          href: '/settings'
        }
      ]

  return (
    <Card className="border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          {loading ? (
            <>
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-28 rounded-full" />
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 text-primary" />
              {variant === 'dashboard' ? tDashboard('quickActions') : tMyPage('quickActions')}
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="divide-y divide-border border-t border-border">
            {[1, 2, 3].map((i) => (
              <div key={i} className="px-4 py-2">
                <Skeleton className="h-8 w-full rounded-lg" />
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-border border-t border-border">
            {actions.map((action, index) => {
              const Icon = action.icon
              const content = (
                <div className={cn(
                  "group relative overflow-hidden px-4 py-2",
                  "hover:bg-primary/5",
                  "transition-all duration-200 ease-out",
                  "cursor-pointer"
                )}>
                  {/* Content */}
                  <div className="relative flex items-center gap-3">
                    {/* Icon circle */}
                    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    
                    {/* Label */}
                    <span className="text-sm font-medium text-foreground">
                      {action.label}
                    </span>
                    
                    {/* Arrow indicator - always visible */}
                    <div className="ml-auto transition-transform duration-200 group-hover:translate-x-1">
                      <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )

              if (action.onClick) {
                return (
                  <div key={action.id}>
                    <button
                      onClick={action.onClick}
                      className="w-full text-left"
                    >
                      {content}
                    </button>
                  </div>
                )
              }

              return (
                <div key={action.id}>
                  <Link href={action.href || '#'}>
                    {content}
                  </Link>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
